import React, { useState, useEffect } from 'react'
import { LEG_OPTIONS, PICKEM_SPORTS, SPORT_DISPLAY_NAMES, DEFAULT_FILTERS } from './pickEmConfig'
import '../styles/app.css'

const SPORT_GROUPS = [
  { label: 'Basketball', sports: ['NBA', 'WNBA'] },
  { label: 'Baseball',   sports: ['MLB'] },
  { label: 'American Football', sports: ['NFL'] },
  { label: 'Ice Hockey', sports: ['NHL'] },
  { label: 'Aussie Rules', sports: ['AFL'] },
]

// ─── Shared inputs ────────────────────────────────────────────────────────────

const NumericInput = ({ value, onChange, defaultValue = 0, ...props }) => {
  const [draft, setDraft] = useState(String(value))
  useEffect(() => { setDraft(String(value)) }, [value])
  return (
    <input
      {...props}
      type="number"
      className="bc-range-input"
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={() => {
        const n = parseFloat(draft)
        const final = isNaN(n) ? defaultValue : n
        setDraft(String(final))
        onChange(final)
      }}
    />
  )
}

function CollapsibleSection({ label, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="bc-filter-section">
      <button className="bc-collapsible-header" onClick={() => setOpen(o => !o)}>
        <span className="bc-filter-label" style={{ margin: 0 }}>{label}</span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          {open ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
        </svg>
      </button>
      {open && <div className="bc-collapsible-body">{children}</div>}
    </div>
  )
}

function SportGroup({ group, filters, setFilters }) {
  const [open, setOpen] = useState(false)
  const available = group.sports.filter(s => PICKEM_SPORTS.includes(s))
  if (available.length === 0) return null

  const allOn  = available.every(s => filters.sports.has(s))
  const allOff = available.every(s => !filters.sports.has(s))

  const toggleGroup = (e) => {
    e.stopPropagation()
    setFilters(prev => {
      const next = new Set(prev.sports)
      if (allOn) {
        available.forEach(s => { if (next.size > 1) next.delete(s) })
      } else {
        available.forEach(s => next.add(s))
      }
      return { ...prev, sports: next }
    })
  }

  const toggleSport = (sport) => {
    setFilters(prev => {
      const next = new Set(prev.sports)
      if (next.has(sport) && next.size === 1) return prev
      next.has(sport) ? next.delete(sport) : next.add(sport)
      return { ...prev, sports: next }
    })
  }

  return (
    <div className={`bc-sgroup${open ? ' open' : ''}`}>
      <div className="bc-sgroup-header" onClick={() => setOpen(o => !o)}>
        <div className="bc-sgroup-left">
          <input
            type="checkbox"
            checked={allOn}
            ref={el => { if (el) el.indeterminate = !allOn && !allOff }}
            onChange={toggleGroup}
            onClick={e => e.stopPropagation()}
          />
          <span className="bc-sgroup-name">{group.label}</span>
        </div>
        <div className="bc-sgroup-right">
          <svg className="bc-sgroup-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            {open ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
          </svg>
        </div>
      </div>
      {open && (
        <div className="bc-sgroup-items">
          {available.map(s => (
            <label key={s} className="bc-check-item">
              <input type="checkbox" checked={filters.sports.has(s)} onChange={() => toggleSport(s)} />
              {SPORT_DISPLAY_NAMES[s] || s}
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function PickEmFilterPanel({
  isOpen, onClose,
  filters, setFilters,
  savedFilters, activeFilterId, filterName, setFilterName,
  onSaveFilter, onLoadFilter, onDeleteFilter, onToggleDefault,
  sortBy, setSortBy, sortDir, setSortDir,
}) {
  if (!isOpen) return null

  const reset = () => setFilters({
    ...DEFAULT_FILTERS,
    sports: new Set(DEFAULT_FILTERS.sports),
  })

  return (
    <>
      <div className="bc-overlay" onClick={onClose} />
      <div className="bc-filter-panel">

        {/* Header */}
        <div className="bc-panel-header">
          <p className="bc-panel-title">Filters</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              className="bc-btn bc-reset-header-btn"
              onClick={reset}
              style={{ fontSize: '12px', padding: '5px 10px' }}
            >
              Reset to default
            </button>
            <button className="bc-panel-close" onClick={onClose}>×</button>
          </div>
        </div>

        <div className="bc-panel-body">

          {/* Saved Filters */}
          <div className="bc-filter-section">
            <span className="bc-filter-label">Saved Filters</span>
            {savedFilters.length === 0 ? (
              <p className="bc-saved-empty">Set your filters below, then save them at the bottom.</p>
            ) : (
              <div className="bc-saved-list">
                {savedFilters.map(f => (
                  <div
                    key={f.id}
                    className={`bc-saved-item${f.isDefault ? ' is-default' : ''}${activeFilterId === f.id ? ' is-active' : ''}`}
                    onClick={() => onLoadFilter(f)}
                    style={{ cursor: 'pointer' }}
                  >
                    <span className="bc-saved-load">{f.name}</span>
                    <div className="bc-saved-actions">
                      <button
                        className={`bc-saved-star${f.isDefault ? ' active' : ''}`}
                        onClick={e => { e.stopPropagation(); onToggleDefault(f.id) }}
                        title={f.isDefault ? 'Remove default' : 'Set as default'}
                      >★</button>
                      <button
                        className="bc-saved-delete"
                        onClick={e => { e.stopPropagation(); onDeleteFilter(f.id) }}
                        title="Delete"
                      >×</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Leg Count */}
          <div className="bc-filter-section">
            <span className="bc-filter-label">Leg Count</span>
            <select
              className="bc-select"
              value={filters.legCount}
              onChange={e => setFilters(prev => ({ ...prev, legCount: e.target.value }))}
            >
              {LEG_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label} · {opt.threshold.toFixed(3)} threshold · {opt.payout}x payout
                </option>
              ))}
            </select>
          </div>

          {/* Sports */}
          <CollapsibleSection label="Sports">
            <div className="bc-sgroup-list">
              {SPORT_GROUPS.map(group => (
                <SportGroup
                  key={group.label}
                  group={group}
                  filters={filters}
                  setFilters={setFilters}
                />
              ))}
            </div>
          </CollapsibleSection>

          {/* EV% */}
          <div className="bc-filter-section">
            <span className="bc-filter-label">EV %</span>
            <div className="bc-range-row">
              <div className="bc-range-field">
                <span className="bc-range-sublabel">Min</span>
                <NumericInput
                  value={filters.ev[0]}
                  defaultValue={0}
                  onChange={v => setFilters(prev => ({ ...prev, ev: [v, prev.ev[1]] }))}
                />
              </div>
              <div className="bc-range-field">
                <span className="bc-range-sublabel">Max</span>
                <NumericInput
                  value={filters.ev[1]}
                  defaultValue={50}
                  onChange={v => setFilters(prev => ({ ...prev, ev: [prev.ev[0], v] }))}
                />
              </div>
            </div>
          </div>

          {/* Starts In */}
          <div className="bc-filter-section">
            <span className="bc-filter-label">Starts In (hours)</span>
            <div className="bc-range-row">
              <div className="bc-range-field">
                <span className="bc-range-sublabel">Min</span>
                <NumericInput
                  value={filters.hoursUntil[0]}
                  defaultValue={0}
                  onChange={v => setFilters(prev => ({ ...prev, hoursUntil: [v, prev.hoursUntil[1]] }))}
                />
              </div>
              <div className="bc-range-field">
                <span className="bc-range-sublabel">Max</span>
                <NumericInput
                  value={filters.hoursUntil[1]}
                  defaultValue={24}
                  onChange={v => setFilters(prev => ({ ...prev, hoursUntil: [prev.hoursUntil[0], v] }))}
                />
              </div>
            </div>
          </div>

          {/* Min Bookmakers */}
          <div className="bc-filter-section">
            <span className="bc-filter-label">Min Bookmakers</span>
            <NumericInput
              value={filters.minBooks}
              defaultValue={1}
              min={1}
              style={{ width: '80px' }}
              onChange={v => setFilters(prev => ({ ...prev, minBooks: v }))}
            />
          </div>

          {/* Sort — only rendered on mobile when setSortBy is passed */}
          {setSortBy && (
            <div className="bc-filter-section">
              <span className="bc-filter-label">Sort By</span>
              <select
                className="bc-select"
                value={`${sortBy}:${sortDir}`}
                onChange={e => {
                  const [col, dir] = e.target.value.split(':')
                  setSortBy(col); setSortDir(dir)
                }}
              >
                <option value="ev:desc">EV% ↓ (High to Low)</option>
                <option value="ev:asc">EV% ↑ (Low to High)</option>
                <option value="fair_odds:desc">Fair Odds ↓ (High to Low)</option>
                <option value="fair_odds:asc">Fair Odds ↑ (Low to High)</option>
                <option value="win_prob:desc">Win Probability ↓</option>
                <option value="commence_time:asc">Start Time ↑ (Soonest)</option>
                <option value="commence_time:desc">Start Time ↓ (Latest)</option>
                <option value="bookmaker_count:desc">Most Bookmakers ↓</option>
              </select>
            </div>
          )}

        </div>

        {/* Footer — save filter */}
        <div className="bc-panel-footer">
          <div className="bc-save-row">
            <input
              className="bc-range-input"
              placeholder="Filter name..."
              value={filterName}
              onChange={e => setFilterName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && onSaveFilter(filterName)}
            />
            <button className="bc-btn" onClick={() => onSaveFilter(filterName)}>Save Current</button>
          </div>
          <div className="bc-footer-actions">
            <button className="bc-btn bc-reset-footer-btn" onClick={reset}>Reset</button>
            <button className="bc-btn bc-apply-btn" onClick={onClose}>Apply</button>
          </div>
        </div>

      </div>
    </>
  )
}
